import React, { FC, useCallback, useEffect, useState } from 'react';
import Select, { InputProps, Options, components } from 'react-select';
import { selectStyles } from '../selectValue/styles';
import { PlusButton } from 'shared/ui/buttons/iconButtons/plusButton';
import { MinusButton } from 'shared/ui/buttons/iconButtons/minusButton';
import { TNewValue } from 'shared/types';
import { useDebounceCallback } from 'shared/lib/debounce';

interface Option {
	value: string;
	label: string;
}

interface SelectValueProps {
	options?: Options<Option>;
	defaultValue?: Option | null;
	onChangeOption?: (newValue: TNewValue) => void;
}

export const SelectIncrement: FC<SelectValueProps> = React.memo(({
	options = [],
	defaultValue,
	onChangeOption,
}) => {
	const [selectedOption, setSelectedOption] = useState<Option | null>(
		defaultValue || null,
	);
	const [isOpen, setOpen] = useState(false);

	const debounceChangeOption = useDebounceCallback(
		(option: Option | null) => {
			onChangeOption?.(option);
		},
		500,
	);

	const selectOption = useCallback(
		(option: Option | null) => {
			if (onChangeOption) {
				setSelectedOption(option);
				debounceChangeOption(option);
			}
		},
		[setSelectedOption, onChangeOption, debounceChangeOption],
	);

	const values = options.map(option => parseInt(option.value, 10));
	const min = Math.min(...values);
	const max = Math.max(...values);

	const updateSelection = useCallback(
		(newValue: number) => {
			const newOption = options.find(
				option => parseInt(option.value, 10) === newValue,
			);
			selectOption(newOption || null);
			setOpen(false);
		},
		[options, selectOption],
	);

	const handleIncrement = useCallback(() => {
		if (!selectedOption) return;

		const currentValue = parseInt(selectedOption.value, 10);
		const nextValue = Math.min(currentValue + 1, max);
		updateSelection(nextValue);
	}, [selectedOption, max, updateSelection]);

	const handleDecrement = useCallback(() => {
		if (!selectedOption) return;

		const currentValue = parseInt(selectedOption.value, 10);
		const prevValue = Math.max(currentValue - 1, min);
		updateSelection(prevValue);
	}, [selectedOption, min, updateSelection]);

	const openMenu = useCallback(() => {
		setOpen(true);
	}, []);

	const closeMenu = useCallback(() => {
		setOpen(false);
	}, []);

	const CustomControl = (props: any) => {
		const { children } = props;

		return (
			<components.Control {...props} innerProps={{ ...props.innerProps }}>
				<MinusButton onClick={handleDecrement} />
				<div
					onClick={!window.matchMedia("(pointer: coarse)").matches ? openMenu : undefined}
					onTouchStart={openMenu}
					style={{ flex: 1, textAlign: 'center', cursor: 'pointer' }}
				>
					{children}
				</div>
				<PlusButton onClick={handleIncrement} />
			</components.Control>
		);
	};

	const CustomInput: any = (props: InputProps) => {
		const onInputChange = (newValue: string) => {
			let value = newValue.replace(/[^\d]/, '');
			if (value.startsWith('0')) {
				value = value.replace('0', '');
			}

			if (+value > 999) {
				return;
			}

			props.selectProps.onInputChange(value, {} as any)
		}

		return (
			<components.Input {...props} value={props.value} onChange={(e) => onInputChange(e.target.value)} />
		);
	};

	useEffect(() => {
		if (defaultValue) {
			setSelectedOption(defaultValue);
			if (onChangeOption) {
				onChangeOption(defaultValue);
			}
		}
	}, [defaultValue, onChangeOption]);

	return (
		<Select
			styles={selectStyles}
			value={selectedOption}
			onChange={selectOption}
			isMulti={false}
			options={options}
			components={{
				Control: CustomControl,
				DropdownIndicator: null,
				Input: CustomInput,
			}}
			onMenuClose={closeMenu}
			menuIsOpen={isOpen}
		/>
	);
}, (prevProps, nextProps) => {
	if (prevProps.defaultValue === nextProps.defaultValue) {
		return true;
	}

	return false;
});
